// Desglose de estados de las reuniones (statusCounts) que sirve meeting-api.
// `statusCounts` es un mapa { estado: número } all-time: solo aparecen los estados
// presentes para ese usuario, así que un estado ausente = 0 (no asumir las 14 claves).
//
// Cada estado se cataloga por:
//  - `phase`:    fase del pipeline (Transcripción / Resumen / Audio / Otros).
//  - `label`:    nombre legible en español para el detalle.
//  - `group`:    categoría semántica para el resumen rápido (success/error/inProgress/standBy).
//
// El pipeline de una reunión es audio → transcripción → resumen. El único éxito
// "terminal" es summary_succeed; los *_succeed intermedios (transcription/audio) se
// consideran en curso porque la reunión aún no está completamente procesada.

export const MEETING_STATUS_CATALOG = {
  // Transcripción
  transcription_started: { phase: 'Transcripción', label: 'Transcripción iniciada', group: 'inProgress' },
  transcription_in_progress: { phase: 'Transcripción', label: 'Transcripción en curso', group: 'inProgress' },
  transcription_succeed: { phase: 'Transcripción', label: 'Transcripción completada', group: 'inProgress' },
  transcription_error: { phase: 'Transcripción', label: 'Error de transcripción', group: 'error' },
  // Resumen
  summary_started: { phase: 'Resumen', label: 'Resumen iniciado', group: 'inProgress' },
  summary_in_progress: { phase: 'Resumen', label: 'Resumen en curso', group: 'inProgress' },
  summary_succeed: { phase: 'Resumen', label: 'Resumen completado', group: 'success' },
  summary_error: { phase: 'Resumen', label: 'Error de resumen', group: 'error' },
  // Audio
  audio_started: { phase: 'Audio', label: 'Audio iniciado', group: 'inProgress' },
  audio_in_progress: { phase: 'Audio', label: 'Audio en curso', group: 'inProgress' },
  audio_succeed: { phase: 'Audio', label: 'Audio completado', group: 'inProgress' },
  audio_error: { phase: 'Audio', label: 'Error de audio', group: 'error' },
  // Otros
  server_error: { phase: 'Otros', label: 'Error de servidor', group: 'error' },
  stand_by: { phase: 'Otros', label: 'En espera', group: 'standBy' },
};

// Categorías semánticas para el resumen rápido. `color` = color de paleta MUI.
export const MEETING_STATUS_GROUPS = {
  success: { label: 'Éxito', color: 'success', icon: 'eva:checkmark-circle-2-fill' },
  error: { label: 'Errores', color: 'error', icon: 'eva:alert-triangle-fill' },
  inProgress: { label: 'En curso', color: 'warning', icon: 'eva:clock-fill' },
  standBy: { label: 'En espera', color: 'default', icon: 'eva:pause-circle-fill' },
};

const GROUP_ORDER = ['success', 'inProgress', 'standBy', 'error'];

// Normaliza un statusCounts crudo en una estructura lista para pintar:
//  { total, groups: [{ key, label, color, icon, count, items: [{ key, label, count }] }] }
// Solo incluye grupos/estados con count > 0, en orden estable. Tolera null/undefined.
export function summarizeMeetingStatus(statusCounts) {
  const counts = statusCounts && typeof statusCounts === 'object' ? statusCounts : {};
  const groups = {};
  let total = 0;

  Object.entries(counts).forEach(([status, raw]) => {
    const count = Number(raw) || 0;
    if (count <= 0) return;
    total += count;
    const meta = MEETING_STATUS_CATALOG[status] || { phase: 'Otros', label: status, group: 'standBy' };
    if (!groups[meta.group]) groups[meta.group] = { items: [], count: 0 };
    groups[meta.group].items.push({ key: status, label: meta.label, count });
    groups[meta.group].count += count;
  });

  const ordered = GROUP_ORDER.filter((g) => groups[g]).map((g) => {
    const info = MEETING_STATUS_GROUPS[g];
    return {
      key: g,
      label: info.label,
      color: info.color,
      icon: info.icon,
      count: groups[g].count,
      items: groups[g].items.sort((a, b) => b.count - a.count),
    };
  });

  return { total, groups: ordered };
}

// ¿Hay algo que mostrar? (evita renderizar tooltips vacíos)
export function hasMeetingStatus(statusCounts) {
  return summarizeMeetingStatus(statusCounts).total > 0;
}

// "Procesado" = el pipeline llegó al final: `summary_succeed`. OJO: no confundir con
// `transcription_succeed` / `audio_succeed` (etapas intermedias, la reunión aún no está
// procesada) ni con el total de reuniones creadas (una reunión puede quedarse en error).
export const PROCESSED_MEETING_STATUS = 'summary_succeed';

// Nº de reuniones que un usuario ha llegado a procesar, sumando las vivas y las borradas:
// haberla borrado después no deshace la activación.
export function processedMeetingCount(statusCounts, deletedStatusCounts) {
  const read = (counts) => (counts && typeof counts === 'object' ? Number(counts[PROCESSED_MEETING_STATUS]) || 0 : 0);
  return read(statusCounts) + read(deletedStatusCounts);
}

// Un usuario está "activado" cuando ha procesado al menos una reunión.
export function isActivatedUser(statusCounts, deletedStatusCounts) {
  return processedMeetingCount(statusCounts, deletedStatusCounts) > 0;
}
